import React, { FC, useMemo } from "react";
import { useActionSheet } from "@app/overlay";
import { Button } from "@shared/ui/button";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import {
    BusinessBoStateValue,
    BusinessDivisionValue,
    BusinessDateValue,
    BusinessMeasureValue,
    BusinessStockObjValue,
    formatBusinessDateText,
    formatDivisionText,
    formatMeasureValueText,
    formatStockObjText,
} from "@src/components/business-data-display";
import { CardLoader } from "@src/components/card-loader";
import type { LimitCard } from "@src/api/stock/limitCards";
import { useBoAvOperations } from "@src/api/bo";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useWsLimitCardMutations } from "../../useWsLimitCardMutations";
import { useWsLimitCardLeaveQuery } from "../../hooks/useWsLimitCardLeaveQuery";
import "./WsLeaveCardContent.scss";

export interface WsLeaveCardContentProps {
    leaveCode: number;
    limitCard: LimitCard;
}

export const WsLeaveCardContent: FC<WsLeaveCardContentProps> = ({ leaveCode, limitCard }) => {
    const actionSheet = useActionSheet();
    const leaveQuery = useWsLimitCardLeaveQuery(leaveCode);
    const leave = leaveQuery.data;

    const avOperationsQuery = useBoAvOperations(leaveCode, leaveCode > 0);

    const mutations = useWsLimitCardMutations({
        onBoOperationSuccess: () => {
            void leaveQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: leave,
        confirmAndPerform,
        buildParams: (operation, leaveItem) => ({
            operation,
            boCode: leaveItem.Code,
            documentLabel: `Отпуск от ${formatBusinessDateText(leaveItem.LeaveDate)}`,
            fields: [
                { label: "№ лимитной карты", value: limitCard.Num },
                { label: "Отправитель", value: formatDivisionText(limitCard.Sender, limitCard.SenderSection) },
                { label: "Получатель", value: formatDivisionText(limitCard.Receiver, limitCard.ReceiverSection) },
                { label: "Номенклатура", value: formatStockObjText(leaveItem.LimitCardContent?.StockObj) },
                {
                    label: "Отпущено",
                    value: formatMeasureValueText(leaveItem.Qty, leaveItem.LimitCardContent?.MeasMain),
                },
            ],
        }),
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(
        () => [
            {
                id: "bo-operations",
                items: operationItems,
            },
        ],
        [operationItems],
    );

    const deficitLimitCard = useMemo(() => {
        if (leave?.LimitCardContent == null) {
            return null;
        }

        return leave.LimitCardContent.Qty1 - leave.LimitCardContent.LeaveQty;
    }, [leave]);

    if (leave == null || leaveQuery.isFetching) {
        return <CardLoader btns />;
    }

    return (
        <div className="ws_leave_card_content">
            {operationItems.length > 0 && (
                <Button
                    variant="secondary"
                    onClick={() => actionSheet.open({ title: "Операции", sections: actionSheetSections })}
                >
                    Выполнить операцию
                </Button>
            )}
            <div className="ws_leave_card_content__section">
                <h3 className="ws_leave_card_content__title">Лимитная карта</h3>
                <DetailList className="ws_leave_card_content__details">
                    <DetailList.Row
                        label={"Отправитель"}
                        value={<BusinessDivisionValue division={limitCard.Sender} section={limitCard.SenderSection} />}
                    />
                    <DetailList.Row
                        label={"Получатель"}
                        value={<BusinessDivisionValue division={limitCard.Receiver} section={limitCard.ReceiverSection} />}
                    />
                </DetailList>

                <h3 className="ws_leave_card_content__title">Состав</h3>
                <DetailList className="ws_leave_card_content__details">
                    <DetailList.Row
                        label={"Номенклатура"}
                        value={<BusinessStockObjValue stockObj={leave.LimitCardContent?.StockObj} />}
                    />
                    <DetailList.Row
                        label={"Отпущено"}
                        value={
                            <BusinessMeasureValue
                                value={leave.LimitCardContent?.LeaveQty}
                                measure={leave.LimitCardContent?.MeasMain}
                            />
                        }
                    />
                    <DetailList.Row
                        label={"Лимит"}
                        value={
                            <BusinessMeasureValue
                                value={leave.LimitCardContent?.Qty1}
                                measure={leave.LimitCardContent?.MeasMain}
                            />
                        }
                    />
                    <DetailList.Row
                        label={"Дефицит"}
                        value={<BusinessMeasureValue value={deficitLimitCard} measure={leave.LimitCardContent?.MeasMain} />}
                    />
                </DetailList>

                <h3 className="ws_leave_card_content__title">Отпуск</h3>
                <DetailList className="ws_leave_card_content__details">
                    <DetailList.Row label={"Дата отпуска"} value={<BusinessDateValue value={leave.LeaveDate} />} />
                    <DetailList.Row
                        label={"Статус"}
                        value={<BusinessBoStateValue state={leave.Bo?.State} labelSource="short" />}
                    />
                    <DetailList.Row
                        label={"Отпущено"}
                        value={<BusinessMeasureValue value={leave.Qty} measure={leave.LimitCardContent?.MeasMain} />}
                    />
                </DetailList>
            </div>
        </div>
    );
};
